import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import MobileNav from "../components/layout/MobileNav";
import NotificationToggle from "../components/NotificationToggle";
import {
  Mountain,
  FileText,
  AlertTriangle,
  Home,
  DollarSign,
  LogOut,
  ChevronRight,
  Building2,
  ClipboardCheck,
  MessageSquare,
  User,
  CreditCard,
  Map,
  Megaphone,
  Bell,
  Calendar,
  Sparkles,
  Send,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  MessageCircle,
  CalendarDays,
  AlertCircle,
  Loader2,
  Camera
} from "lucide-react";

const OwnerPortal2 = () => {
  const { user, token, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchDashboard();
    fetchNotifications();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/owner-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/owner-portal/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const handleAiQuery = async (queryType) => {
    setAiLoading(true);
    setAiResponse(null);
    try {
      let endpoint = "";
      if (queryType === "summary") {
        endpoint = "/owner-portal/ai/summarize-account";
      } else if (queryType === "next-steps") {
        endpoint = "/owner-portal/ai/next-steps";
      }
      
      const res = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiResponse(res.data);
    } catch (error) {
      setAiResponse({ error: "Unable to get AI response. Please try again." });
    } finally {
      setAiLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/owner-portal/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "bg-amber-100 text-amber-800",
      resolved: "bg-green-100 text-green-800",
      approved: "bg-green-100 text-green-800",
      denied: "bg-red-100 text-red-800",
      pending: "bg-blue-100 text-blue-800",
      submitted: "bg-blue-100 text-blue-800",
      under_review: "bg-purple-100 text-purple-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20 md:pb-0">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0" data-testid="owner-portal-2">
      {/* Desktop Header - Hidden on mobile */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
              <Mountain className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-serif text-lg font-semibold text-foreground">Horse Mountain Ranch</span>
              <span className="text-muted-foreground text-sm ml-2">Owner Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative" asChild>
              <Link to="/owner/notifications">
                <Bell className="w-5 h-5" />
                {dashboard?.notifications?.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-secondary text-white text-xs rounded-full flex items-center justify-center">
                    {dashboard.notifications.unread_count}
                  </span>
                )}
              </Link>
            </Button>
            <span className="text-sm text-muted-foreground">
              {user?.name || user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>
      
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur md:hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center">
              <Mountain className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-base font-semibold">Horse Mountain</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationToggle compact />
            <Button variant="ghost" size="icon" className="relative h-8 w-8" asChild>
              <Link to="/owner/notifications">
                <Bell className="w-4 h-4" />
                {dashboard?.notifications?.unread_count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-secondary text-white text-[10px] rounded-full flex items-center justify-center">
                    {dashboard.notifications.unread_count}
                  </span>
                )}
              </Link>
            </Button>
            <Link to="/owner/profile">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
        {/* Welcome Section */}
        <div className="mb-4 md:mb-8">
          <h1 className="font-serif text-2xl md:text-4xl font-bold text-foreground mb-1 md:mb-2">
            Welcome back, {user?.name?.split(" ")[0] || "Owner"}
          </h1>
          <p className="text-muted-foreground text-lg">
            Here's everything you need to know about your property and community.
          </p>
        </div>

        {/* Emergency Alerts Banner */}
        {dashboard?.community_updates?.emergency_alerts?.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">Emergency Alert</p>
                <p className="text-sm text-red-700">
                  {dashboard.community_updates.emergency_alerts[0]?.title}
                </p>
                <Link to="/owner/alerts" className="text-sm text-red-600 hover:underline mt-1 inline-block">
                  View Details →
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.my_property?.lot_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Properties</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatCurrency(dashboard?.my_balance?.total_due)}</p>
                      <p className="text-xs text-muted-foreground">Balance Due</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.my_requests?.violation_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Notices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.my_requests?.drc_pending || 0}</p>
                      <p className="text-xs text-muted-foreground">Pending DRC</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabbed Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white border">
                <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="requests" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  My Requests
                </TabsTrigger>
                <TabsTrigger value="payments" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  Payments
                </TabsTrigger>
                <TabsTrigger value="community" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  Community
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-4 space-y-4">
                {/* My Property Card */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Home className="w-5 h-5 text-primary" />
                        My Property
                      </CardTitle>
                      <Link to="/owner/gis">
                        <Button variant="ghost" size="sm" className="text-primary">
                          <Map className="w-4 h-4 mr-1" /> View Map
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.my_property?.properties?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.my_property.properties.map((prop, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                <MapPin className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">Lot {prop.lot_number}</p>
                                <p className="text-sm text-muted-foreground">{prop.address || "No address on file"}</p>
                              </div>
                            </div>
                            <Badge variant="outline">{prop.status || "Active"}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No properties linked to your account.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Link to="/owner/documents">
                    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-full">
                      <CardContent className="pt-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                          <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="font-medium text-sm">Documents</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/owner/drc">
                    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-full">
                      <CardContent className="pt-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                          <ClipboardCheck className="w-6 h-6 text-orange-600" />
                        </div>
                        <p className="font-medium text-sm">Design Review</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/owner/forum">
                    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-full">
                      <CardContent className="pt-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                          <MessageSquare className="w-6 h-6 text-purple-600" />
                        </div>
                        <p className="font-medium text-sm">Forum</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/owner/events">
                    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-full">
                      <CardContent className="pt-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                          <CalendarDays className="w-6 h-6 text-green-600" />
                        </div>
                        <p className="font-medium text-sm">Events</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/owner/report-concern">
                    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-full relative">
                      <CardContent className="pt-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                          <Camera className="w-6 h-6 text-violet-600" />
                        </div>
                        <p className="font-medium text-sm">Report Concern</p>
                        <Badge className="absolute top-1 right-1 text-[9px] bg-violet-500">BETA</Badge>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </TabsContent>

              {/* My Requests Tab */}
              <TabsContent value="requests" className="mt-4 space-y-4">
                {/* Notices */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        Open Notices
                      </CardTitle>
                      <Link to="/owner/violations">
                        <Button variant="ghost" size="sm">View All →</Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.my_requests?.violations?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.my_requests.violations.slice(0, 3).map((v, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div>
                              <p className="font-medium capitalize">{v.category?.replace(/_/g, " ")}</p>
                              <p className="text-sm text-muted-foreground">{v.description?.slice(0, 50)}...</p>
                            </div>
                            <div className="text-right">
                              <Badge className={getStatusColor(v.status)}>{v.status}</Badge>
                              {v.cure_deadline && (
                                <p className="text-xs text-red-600 mt-1">Due: {formatDate(v.cure_deadline)}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <p className="text-green-700">No open notices. Keep up the great work!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* DRC Submissions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5 text-blue-600" />
                        DRC Submissions
                      </CardTitle>
                      <Link to="/owner/drc">
                        <Button variant="ghost" size="sm">View All →</Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.my_requests?.drc_submissions?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.my_requests.drc_submissions.slice(0, 3).map((d, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <div>
                              <p className="font-medium capitalize">{d.project_type?.replace(/_/g, " ")}</p>
                              <p className="text-sm text-muted-foreground">{d.description?.slice(0, 50)}...</p>
                            </div>
                            <Badge className={getStatusColor(d.status)}>{d.status?.replace(/_/g, " ")}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No DRC submissions on file.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="mt-4 space-y-4">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                      Account Balance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold">{formatCurrency(dashboard?.my_balance?.current_dues)}</p>
                        <p className="text-sm text-muted-foreground">Current Dues</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboard?.my_balance?.pending_fines)}</p>
                        <p className="text-sm text-muted-foreground">Pending Fines</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{formatCurrency(dashboard?.my_balance?.total_due)}</p>
                        <p className="text-sm text-muted-foreground">Total Due</p>
                      </div>
                    </div>
                    
                    {(dashboard?.my_balance?.total_due || 0) > 0 && (
                      <Link to="/owner/payments">
                        <Button className="w-full bg-primary hover:bg-primary/90">
                          <CreditCard className="w-4 h-4 mr-2" /> Pay Now
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Payments */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Recent Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.my_balance?.recent_payments?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboard.my_balance.recent_payments.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 border-b last:border-0">
                            <div>
                              <p className="font-medium">{p.description || "Payment"}</p>
                              <p className="text-sm text-muted-foreground">{formatDate(p.date)}</p>
                            </div>
                            <p className="font-medium text-green-600">-{formatCurrency(p.amount)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No recent payments.</p>
                    )}
                    <Link to="/owner/payments" className="block mt-4">
                      <Button variant="outline" className="w-full">View Payment History</Button>
                    </Link>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Community Tab */}
              <TabsContent value="community" className="mt-4 space-y-4">
                {/* Announcements */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-indigo-600" />
                        Announcements
                      </CardTitle>
                      <Link to="/owner/announcements">
                        <Button variant="ghost" size="sm">View All →</Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.community_updates?.announcements?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.community_updates.announcements.slice(0, 3).map((a, idx) => (
                          <div key={idx} className="p-3 bg-indigo-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <p className="font-medium">{a.title}</p>
                              <Badge variant="outline" className="text-xs">{a.category}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{a.content?.slice(0, 100)}...</p>
                            <p className="text-xs text-muted-foreground mt-2">{formatDate(a.published_at)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No recent announcements.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Upcoming Meetings */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        Upcoming Meetings
                      </CardTitle>
                      <Link to="/owner/meetings">
                        <Button variant="ghost" size="sm">View All →</Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.community_updates?.meetings?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.community_updates.meetings.map((m, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-purple-100 rounded-lg flex flex-col items-center justify-center">
                                <span className="text-xs font-medium text-purple-600">
                                  {new Date(m.date).toLocaleDateString("en-US", { month: "short" })}
                                </span>
                                <span className="text-lg font-bold text-purple-700">
                                  {new Date(m.date).getDate()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium capitalize">{m.type} Meeting</p>
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(m.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No upcoming meetings scheduled.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Right column */}
          <div className="space-y-6">
            {/* AI Assistant Card */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-primary to-primary/80 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#B87333]" />
                  AI Assistant
                </CardTitle>
                <CardDescription className="text-white/70">
                  Get instant help with your HOA questions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="secondary"
                  className="w-full justify-start bg-white/10 hover:bg-white/20 text-white border-0"
                  onClick={() => handleAiQuery("summary")}
                  disabled={aiLoading}
                  data-testid="ai-summary-btn"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                  Summarize my account
                </Button>
                <Button
                  variant="secondary"
                  className="w-full justify-start bg-white/10 hover:bg-white/20 text-white border-0"
                  onClick={() => handleAiQuery("next-steps")}
                  disabled={aiLoading}
                  data-testid="ai-nextsteps-btn"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HelpCircle className="w-4 h-4 mr-2" />}
                  What do I need to do?
                </Button>
                
                {/* Quick Questions Section */}
                <div className="pt-2 border-t border-white/20">
                  <p className="text-xs text-white/60 mb-2 uppercase tracking-wide">Quick Questions</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Fence rules?",
                      "Parking rules?",
                      "Next meeting?",
                      "DRC process?"
                    ].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          // This opens the floating chat with the question
                          const event = new CustomEvent('openAIChat', { detail: { question: q } });
                          window.dispatchEvent(event);
                        }}
                        className="px-2 py-1 text-xs bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        data-testid={`quick-q-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
                
                {aiResponse && (
                  <div className="mt-4 p-3 bg-white/10 rounded-lg">
                    {aiResponse.error ? (
                      <p className="text-sm text-red-200">{aiResponse.error}</p>
                    ) : aiResponse.summary ? (
                      <p className="text-sm text-white/90">{aiResponse.summary}</p>
                    ) : aiResponse.next_steps ? (
                      <div className="space-y-2">
                        {aiResponse.next_steps.slice(0, 5).map((step, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <div className={`w-2 h-2 rounded-full mt-1.5 ${
                              step.priority === "high" ? "bg-red-400" : 
                              step.priority === "medium" ? "bg-amber-400" : "bg-green-400"
                            }`} />
                            <p className="text-white/90">{step.action}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#B87333]" />
                    Notifications
                  </CardTitle>
                  {dashboard?.notifications?.unread_count > 0 && (
                    <Badge className="bg-[#B87333]">{dashboard.notifications.unread_count} new</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {notifications.length > 0 ? (
                    <div className="space-y-2">
                      {notifications.map((n, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            n.read ? "bg-gray-50" : "bg-blue-50 border-l-4 border-blue-500"
                          }`}
                          onClick={() => markNotificationRead(n.notification_id)}
                        >
                          <p className="font-medium text-sm">{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{n.message?.slice(0, 60)}...</p>
                          <p className="text-xs text-muted-foreground mt-1">{formatDate(n.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm text-center py-8">No notifications</p>
                  )}
                </ScrollArea>
                <Link to="/owner/notifications" className="block mt-3">
                  <Button variant="outline" size="sm" className="w-full">View All Notifications</Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/owner/profile" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">My Profile</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
                <Link to="/owner/reservations" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Reservations</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
                <Link to="/owner/documents" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">HOA Documents</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
                <a href="mailto:hoa@horsemountainranch.com" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Contact HOA</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      {/* Mobile Navigation */}
      <MobileNav />
    </div>
  );
};

export default OwnerPortal2;
